<template>
  <div class="product-details-page">
    <headers />

    <!-- 上半部分：左右 50% 比例 Hero 分割区域（完全按照官网 lazzen.com 样式，无 Banner） -->
    <div class="hero-split-container">
      <div class="hero-inner">
        <!-- 左侧：纯白背景，面包屑 + 产品大图/缩略图 -->
        <div class="hero-left">
          <!-- 面包屑 -->
          <div class="breadcrumb-wrap">
            <el-breadcrumb separator-class="el-icon-arrow-right">
              <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
              <el-breadcrumb-item>资料获取</el-breadcrumb-item>
              <el-breadcrumb-item to="/information/productManual"
                >产品手册</el-breadcrumb-item
              >
              <el-breadcrumb-item>{{ details.name }}</el-breadcrumb-item>
            </el-breadcrumb>
          </div>

          <!-- 图片展示区（支持缩略图 + 上下箭头循环轮播 + 右侧主大图） -->
          <div class="gallery-wrap">
            <div class="thumb-column" v-if="imageList.length > 0">
              <!-- 上箭头 -->
              <div class="thumb-arrow arrow-up" @click="prevThumbs">
                <i class="el-icon-arrow-up"></i>
              </div>

              <!-- 缩略图无限平滑向上滑动视口 (固定展示 3 张，物理像素自适应平滑位移) -->
              <div class="thumb-list-window" ref="thumbWindow">
                <div
                  class="thumb-track"
                  :class="{ 'no-transition': !enableTransition }"
                  :style="{
                    transform: `translateY(-${computedTrackOffsetY}px)`,
                  }"
                >
                  <div
                    class="thumb-card"
                    v-for="(item, idx) in displayImageList"
                    :key="`${item.realIndex}-${idx}`"
                    :class="{ active: currentImgIndex === item.realIndex }"
                    @click="onThumbClick(item.realIndex, idx)"
                  >
                    <img :src="item.img" />
                  </div>
                </div>
              </div>

              <!-- 下箭头 -->
              <div class="thumb-arrow arrow-down" @click="nextThumbs">
                <i class="el-icon-arrow-down"></i>
              </div>
            </div>

            <!-- 右侧大图展示区（支持 3 秒自动轮播 + 鼠标左右滑动切页） -->
            <div class="main-img-display">
              <el-carousel
                ref="mainCarousel"
                class="main-carousel"
                height="520px"
                indicator-position="none"
                arrow="hover"
                :interval="3000"
                :initial-index="currentImgIndex"
                @change="handleCarouselChange"
              >
                <el-carousel-item
                  v-for="(img, idx) in imageList"
                  :key="idx"
                  class="carousel-item-box"
                >
                  <div
                    class="carousel-img-wrap"
                    @mousedown="handleMouseDown"
                    @mouseup="handleMouseUp"
                  >
                    <img class="hoverTransform" :src="img" />
                  </div>
                </el-carousel-item>
              </el-carousel>
            </div>
          </div>
        </div>

        <!-- 右侧：淡灰色背景（#f7f8fa），产品大标题 + 放大描述 + 操作按钮 -->
        <div class="hero-right">
          <h1 class="product-title">{{ details.name }}</h1>
          <div
            class="product-desc"
            v-html="details.productDesc || details.breif || ''"
          ></div>

          <div class="hero-divider"></div>

          <div class="hero-btn-group">
            <button class="btn-black" @click="scrollToFiles">样本下载</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 下半部分：Tab 分类与放大版文件列表卡片 -->
    <div
      class="file-section-container"
      ref="fileSection"
      v-if="validTabs.length > 0"
    >
      <!-- 动态 Tab 导航栏（仅在对应字段有数据时才显示该 Tab） -->
      <div class="tab-header">
        <div
          class="tab-item"
          v-for="tab in validTabs"
          :key="tab.key"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.name }}
        </div>
      </div>

      <!-- 文件卡片列表内容 -->
      <div class="tab-content">
        <div class="file-cards-wrapper">
          <div v-for="(file, index) in currentFileList" :key="index">
            <div class="file-card-box" v-if="file.fileName">
              <div class="card-left">
                <!-- 大尺寸 PDF 矢量文件图标 -->
                <img
                  src="https://l-xpert.obs.cn-southwest-2.myhuaweicloud.com/backstage/474112ca-2d02-664e-b0d0-2c24936bf852.svg"
                />

                <div class="card-text">
                  <div
                    class="file-name-title textOverflowOne"
                    :title="file.fileName"
                  >
                    {{ file.fileName || "" }}
                  </div>
                  <div class="file-sub-date">
                    {{ details.updateTime || details.createTime || "" }}
                  </div>
                </div>
              </div>

              <!-- 右侧下载按钮 -->
              <div class="card-right">
                <button
                  class="btn-download-outline"
                  @click="handleDownload(file)"
                >
                  下载 <i class="el-icon-download"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footers />
  </div>
</template>

<script>
import headers from "@/components/header.vue";
import footers from "@/components/footer.vue";
import { productView, fileDownloadRecordAdd } from "@/api/lx";

export default {
  name: "productManualDetails",
  components: {
    headers,
    footers,
  },
  data() {
    return {
      id: "",
      details: {},
      currentImgIndex: 0,
      trackIndex: 2,
      thumbStepHeight: 96,
      enableTransition: true,
      activeTab: "",
      touchStartX: 0,
    };
  },
  watch: {
    /**
     * 监听当前选中图片索引，联动翻动 el-carousel 轮播图
     * @param {number} newVal - 最新选择的图片索引
     */
    currentImgIndex(newVal) {
      if (this.$refs.mainCarousel) {
        this.$refs.mainCarousel.setActiveItem(newVal);
      }
    },
  },
  computed: {
    /**
     * 图片列表数组（合并 detailImage 与主图 image）
     * @returns {Array<string>} 图片 URL 列表
     */
    imageList() {
      let arr = [];
      if (this.details.detailImage) {
        arr = this.details.detailImage.split(",").filter(Boolean);
      }
      if (arr.length === 0 && this.details.image) {
        arr = [this.details.image];
      }
      return arr;
    },

    /**
     * 构建预渲染无缝环形轨道的渲染列表（首尾双向预先渲染 2 张缓冲卡片）
     * 解决滚到最后一张时，下一张图片延迟/等待渲染问题，保证视口 3 张图在 DOM 中时刻提前就绪
     * @returns {Array<Object>} 列表 [{ img, realIndex }]
     */
    displayImageList() {
      const list = this.imageList;
      const total = list.length;
      if (total <= 3) {
        return list.map((img, index) => ({ img, realIndex: index }));
      }

      const prev2Idx = (total - 2 + total) % total;
      const prev1Idx = (total - 1 + total) % total;
      const next0Idx = 0;
      const next1Idx = 1 % total;

      let result = [
        { img: list[prev2Idx], realIndex: prev2Idx },
        { img: list[prev1Idx], realIndex: prev1Idx },
      ];
      list.forEach((img, index) => {
        result.push({ img, realIndex: index });
      });
      result.push({ img: list[next0Idx], realIndex: next0Idx });
      result.push({ img: list[next1Idx], realIndex: next1Idx });

      return result;
    },

    /**
     * 计算平滑轨道的绝对 translateY 像素偏移量
     * 当图片 <= 3 张时，固定在顶端 0px；当图片 > 3 张时，按照 trackIndex 推动平移
     * @returns {number} 偏移像素值
     */
    computedTrackOffsetY() {
      const total = this.imageList.length;
      if (total <= 3) {
        return 0; // 不够 3 张图时，卡片完全装在视口中，轨道置顶 0px，不发生位移错位
      }
      return (this.trackIndex - 1) * this.thumbStepHeight;
    },

    /**
     * 解析产品样本文件 JSON 列表
     * @returns {Array<Object>} 文件列表 [{ fileName, filePath }]
     */
    productSampleList() {
      return this.parseFileList(this.details.productSample);
    },

    /**
     * 解析产品说明书文件 JSON 列表
     * @returns {Array<Object>} 文件列表 [{ fileName, filePath }]
     */
    productManualList() {
      return this.parseFileList(this.details.productManual);
    },

    /**
     * 解析 CCC 证书文件 JSON 列表
     * @returns {Array<Object>} 文件列表 [{ fileName, filePath }]
     */
    cccCertificateList() {
      return this.parseFileList(this.details.cccCertificate);
    },

    /**
     * 解析 CCC 报告文件 JSON 列表
     * @returns {Array<Object>} 文件列表 [{ fileName, filePath }]
     */
    cccReportList() {
      return this.parseFileList(this.details.cccReport);
    },

    /**
     * 有数据的 Tab 栏过滤列表（无数据不显示）
     * @returns {Array<Object>} 包含有效数据的 Tab 列表
     */
    validTabs() {
      const tabs = [];
      if (this.productSampleList && this.productSampleList.length > 0) {
        tabs.push({
          key: "sample",
          name: "产品样本",
          list: this.productSampleList,
        });
      }
      if (this.productManualList && this.productManualList.length > 0) {
        tabs.push({
          key: "manual",
          name: "产品说明书",
          list: this.productManualList,
        });
      }
      if (this.cccCertificateList && this.cccCertificateList.length > 0) {
        tabs.push({
          key: "certificate",
          name: "CCC证书",
          list: this.cccCertificateList,
        });
      }
      if (this.cccReportList && this.cccReportList.length > 0) {
        tabs.push({
          key: "report",
          name: "CCC报告",
          list: this.cccReportList,
        });
      }
      return tabs;
    },

    /**
     * 当前激活 Tab 所对应的文件数组
     * @returns {Array<Object>} 当前选中的文件列表
     */
    currentFileList() {
      const currentTab = this.validTabs.find(
        (tab) => tab.key === this.activeTab
      );
      return currentTab ? currentTab.list : [];
    },
  },
  created() {
    this.id = this.$route.query.id;
    this.getProductView();
  },
  mounted() {
    this.calcStepHeight();
    window.addEventListener("resize", this.calcStepHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calcStepHeight);
  },
  methods: {
    /**
     * 防错解析后端返回的 JSON 格式文件列表
     * @param {string|Array} jsonStr - 转义 JSON 字符串或数组对象
     * @returns {Array<Object>} 文件列表 [{ fileName, filePath }]
     */
    parseFileList(jsonStr) {
      if (!jsonStr) return [];
      try {
        const list =
          typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },

    /**
     * 请求产品详情数据并初始化激活 Tab
     * @returns {void}
     */
    getProductView() {
      productView(this.id).then((res) => {
        this.details = res.data || {};
        this.$nextTick(() => {
          if (this.validTabs.length > 0) {
            this.activeTab = this.validTabs[0].key;
          }
          this.calcStepHeight();
        });
      });
    },

    /**
     * 触发下载或在新窗口中打开文件，并上报产品文件下载埋点记录
     * @param {Object} file - 文件对象
     * @returns {Promise<void>}
     */
    async handleDownload(file) {
      if (!file || !file.filePath) return;
      if (this.id) {
        try {
          await fileDownloadRecordAdd({
            dataId: String(this.id),
            fileType: 1, // 文件类型：1-产品
          });
        } catch (error) {
          console.error("产品资料下载埋点失败:", error);
        }
      }
      const link = document.createElement("a");
      link.href = file.filePath;
      link.target = "_blank";
      link.download = file.fileName || "产品资料文件.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    /**
     * 平滑滚动至文件下载区域
     * @returns {void}
     */
    scrollToFiles() {
      const el = this.$refs.fileSection;
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    },

    /**
     * 动态计算当前屏幕分辨率与响应式布局下卡片的物理精准垂直位移步进值（px）
     * 解决屏幕缩小/响应式调整时死板硬编码 96px 导致的滚动尺寸偏差与错位问题
     * @returns {void}
     */
    calcStepHeight() {
      this.$nextTick(() => {
        const windowEl = this.$refs.thumbWindow;
        if (!windowEl) return;
        const cards = windowEl.querySelectorAll(".thumb-card");
        if (cards && cards.length >= 2) {
          const step = cards[1].offsetTop - cards[0].offsetTop;
          if (step > 0) {
            this.thumbStepHeight = step;
          }
        }
      });
    },

    /**
     * 上翻查看并切换缩略图（向上/向后环形预渲染无缝平滑滑动）
     * @returns {void}
     */
    prevThumbs() {
      const total = this.imageList.length;
      if (total <= 1) return;

      if (total <= 3) {
        this.currentImgIndex = (this.currentImgIndex - 1 + total) % total;
        return;
      }

      this.enableTransition = true;
      this.trackIndex--;
      this.currentImgIndex = (this.currentImgIndex - 1 + total) % total;

      // 若滑到了头部预渲染的补位卡片 (trackIndex === 1)
      if (this.trackIndex === 1) {
        setTimeout(() => {
          this.enableTransition = false;
          this.trackIndex = total + 1; // 默默无缝复位至等价真实索引位置
          this.$nextTick(() => {
            setTimeout(() => {
              this.enableTransition = true;
            }, 50);
          });
        }, 400);
      }
    },

    /**
     * 下翻查看并切换缩略图（向下预渲染真正无限单向平滑滑动，预渲染就绪零等待）
     * @returns {void}
     */
    nextThumbs() {
      const total = this.imageList.length;
      if (total <= 1) return;

      if (total <= 3) {
        this.currentImgIndex = (this.currentImgIndex + 1) % total;
        return;
      }

      this.enableTransition = true;
      this.trackIndex++;
      this.currentImgIndex = (this.currentImgIndex + 1) % total;

      // 若滑到了尾部预渲染的伪第一张 (trackIndex === total + 2)
      if (this.trackIndex === total + 2) {
        setTimeout(() => {
          this.enableTransition = false;
          this.trackIndex = 2; // 默默无缝复位至真实第一张 (下标2)
          this.$nextTick(() => {
            setTimeout(() => {
              this.enableTransition = true;
            }, 50);
          });
        }, 400);
      }
    },

    /**
     * 点击缩略图项触发
     * @param {number} realIndex - 选中的图片真实索引
     * @param {number} listIdx - 渲染卡片的列表下标
     * @returns {void}
     */
    onThumbClick(realIndex, listIdx) {
      const total = this.imageList.length;
      if (total <= 3) {
        this.currentImgIndex = realIndex;
        return;
      }

      this.enableTransition = true;
      if (typeof listIdx === "number") {
        this.trackIndex = listIdx;
      } else {
        this.trackIndex = realIndex + 2;
      }
      this.currentImgIndex = realIndex;
    },

    /**
     * 主图轮播页面切换回调，同步当前索引与无缝轨道
     * @param {number} index - 当前轮播页码索引
     * @returns {void}
     */
    handleCarouselChange(index) {
      if (index === this.currentImgIndex) return;
      const total = this.imageList.length;

      if (total <= 3) {
        this.currentImgIndex = index;
        return;
      }

      // 判断方向：向后切页时驱动 nextThumbs 保持无限向上平滑滚动
      const isForward =
        (index > this.currentImgIndex &&
          !(index === total - 1 && this.currentImgIndex === 0)) ||
        (index === 0 && this.currentImgIndex === total - 1);

      if (isForward) {
        this.nextThumbs();
      } else {
        this.prevThumbs();
      }
    },

    /**
     * 记录鼠标或触摸按下时的 X 坐标（用于支持鼠标左右滑动切图）
     * @param {MouseEvent|TouchEvent} e - 事件对象
     * @returns {void}
     */
    handleMouseDown(e) {
      this.touchStartX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
    },

    /**
     * 鼠标或触摸抬起时计算横向偏移距离，判断左右滑动触发切换
     * @param {MouseEvent|TouchEvent} e - 事件对象
     * @returns {void}
     */
    handleMouseUp(e) {
      const endX =
        e.clientX || (e.changedTouches && e.changedTouches[0].clientX) || 0;
      const deltaX = endX - this.touchStartX;
      if (Math.abs(deltaX) > 35) {
        if (deltaX < 0) {
          // 向左滑动：下一张
          this.nextThumbs();
        } else {
          // 向右滑动：上一张
          this.prevThumbs();
        }
      }
    },
  },
};
</script>

<style lang="less" scoped>
.product-details-page {
  width: 100%;
  background-color: #ffffff;
}

/* 左右 50% 对比分栏（完全按照良信官网 lazzen.com 宽边加高样式） */
.hero-split-container {
  width: 100%;
  background: linear-gradient(to right, #ffffff 50%, #f7f8fa 50%);

  .hero-inner {
    width: 100%;
    max-width: 1760px;
    margin: 0 auto;
    display: flex;
    min-height: 680px;

    @media (max-width: 1800px) {
      padding: 0 40px;
      box-sizing: border-box;
    }

    @media (max-width: 992px) {
      flex-direction: column;
      padding: 0;
      .hero-left,
      .hero-right {
        width: 100% !important;
        padding: 40px 24px !important;
      }
    }

    /* 左侧白色区 */
    .hero-left {
      width: 50%;
      background: #ffffff;
      padding: 50px 60px 60px 40px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      min-height: 600px !important; // 确保放得下缩略图与大图

      .breadcrumb-wrap {
        margin-bottom: 40px;
        /deep/ .el-breadcrumb__item {
          font-size: 14px;
        }
      }

      .gallery-wrap {
        display: flex;
        align-items: center !important;
        flex-wrap: nowrap !important; // 严格禁止换行，确保缩略图与大图并排
        width: 100%;
        gap: 32px;
        flex: 1;
        min-height: 460px !important;

        @media (max-width: 1366px) {
          gap: 20px;
        }

        .thumb-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex: 0 0 84px !important;
          width: 84px !important;
          height: 380px !important;
          min-height: 380px !important;
          flex-shrink: 0 !important;
          overflow: visible !important;

          .thumb-arrow {
            width: 100%;
            height: 24px !important;
            min-height: 24px !important;
            flex: 0 0 24px !important;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #333333 !important;
            font-size: 18px;
            font-weight: 400;
            transition: all 0.2s ease;
            flex-shrink: 0 !important;

            i {
              color: #333333 !important;
              font-size: 20px;
              transition: color 0.2s ease;
            }

            &:hover {
              color: #00d8c3 !important;
              i {
                color: #00d8c3 !important;
              }
            }

            &.disabled {
              color: #333333 !important;
              i {
                color: #333333 !important;
              }
            }
          }

          .thumb-list-window {
            height: 280px !important;
            min-height: 280px !important;
            max-height: 280px !important;
            flex: 0 0 280px !important;
            padding: 2px 0 !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            position: relative;
            width: 84px !important;
            flex-shrink: 0 !important;

            .thumb-track {
              display: flex;
              flex-direction: column;
              gap: 12px;
              transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);

              &.no-transition {
                transition: none !important;
              }
            }
          }

          .thumb-card {
            width: 84px !important;
            height: 84px !important;
            min-height: 84px !important;
            flex: 0 0 84px !important;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
            cursor: pointer;
            padding: 8px;
            box-sizing: border-box;
            background: #ffffff;
            flex-shrink: 0 !important;
            /* 高亮边框与阴影平滑 Fade 过渡 */
            transition: border-color 0.3s ease, box-shadow 0.3s ease,
              transform 0.3s ease;

            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              transition: opacity 0.3s ease;
            }

            &.active,
            &:hover {
              border-color: #00d8c3;
              box-shadow: 0 4px 12px rgba(0, 216, 195, 0.2);
            }
          }
        }

        .main-img-display {
          flex: 1;
          min-width: 0; // 防止 Flex 溢出
          height: 480px;
          max-height: 520px;
          position: relative;
          user-select: none;

          @media (max-width: 1366px) {
            height: 400px;
          }

          /deep/ .el-carousel,
          /deep/ .el-carousel__container {
            height: 100% !important;
            min-height: 400px !important;
            width: 100%;
          }

          .carousel-item-box {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
          }

          .carousel-img-wrap {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: grab;

            &:active {
              cursor: grabbing;
            }

            img {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
              transition: transform 0.4s ease;
            }
          }
        }
      }
    }

    /* 右侧淡灰背景区 (#f7f8fa) */
    .hero-right {
      width: 50%;
      background: #f7f8fa;
      padding: 70px 40px 70px 80px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .product-title {
        font-family: PingFangSC, PingFang SC, "Helvetica Neue", Helvetica, Arial,
          sans-serif;
        font-size: 48px;
        font-weight: 600;
        color: #000000;
        line-height: 1.2;
        margin: 0 0 32px 0;
        letter-spacing: -0.5px;
      }

      .product-desc {
        font-size: 16px;
        color: #444444;
        line-height: 1.95;
        margin-bottom: 12px;
        max-width: 680px;
        text-align: justify;
      }

      .hero-divider {
        width: 100%;
        max-width: 680px;
        height: 1px;
        background-color: #e5e8ed;
        margin-bottom: 50px;
      }

      .hero-btn-group {
        display: flex;
        gap: 20px;

        .btn-black {
          min-width: 140px;
          height: 50px;
          padding: 0 36px;
          background: #333333;
          border: 1px solid #333333;
          border-radius: 4px;
          font-size: 16px;
          font-weight: 500;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.25s ease;

          &:hover {
            background: #00d8c3;
            border-color: #00d8c3;
            color: #ffffff;
            transform: translateY(-1px);
          }
        }

        .btn-outline {
          min-width: 140px;
          height: 50px;
          padding: 0 36px;
          background: #ffffff;
          border: 1px solid #555555;
          border-radius: 4px;
          font-size: 16px;
          font-weight: 500;
          color: #333333;
          cursor: pointer;
          transition: all 0.25s ease;

          &:hover {
            border-color: #00d8c3;
            color: #00d8c3;
            transform: translateY(-1px);
          }
        }
      }
    }
  }
}

/* 下半部分：Tab 分类与文件卡片 */
.file-section-container {
  width: 100%;
  max-width: 1760px;
  margin: 70px auto 120px;
  padding: 0 40px;
  box-sizing: border-box;

  .tab-header {
    display: flex;
    gap: 56px;
    border-bottom: 1px solid #eef3f6;
    padding-bottom: 0px;
    margin-bottom: 36px;

    .tab-item {
      position: relative;
      font-size: 18px;
      font-weight: 500;
      color: #555555;
      padding: 16px 0;
      cursor: pointer;
      transition: color 0.35s ease;

      /* 下划线由中间向两边柔和展开的动画 */
      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 50%;
        width: 0;
        height: 2.5px;
        background: #00d8c3;
        border-radius: 2px;
        transition: all 0.45s cubic-bezier(0.25, 1, 0.5, 1);
        transform: translateX(-50%);
      }

      &:hover,
      &.active {
        color: #00d8c3;

        &::after {
          width: 100%;
        }
      }

      &.active {
        font-weight: 600;
      }
    }
  }

  .file-cards-wrapper {
    display: flex;
    flex-direction: column;
    gap: 18px;

    .file-card-box {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f7f8fa;
      border-radius: 10px;
      padding: 28px 40px;
      transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);

      .card-left {
        display: flex;
        align-items: center;
        gap: 24px;

        .pdf-icon-svg {
          flex-shrink: 0;
          transition: transform 0.35s ease;
        }

        .card-text {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .file-name-title {
            font-size: 17px;
            font-weight: 500;
            color: #111111;
            max-width: 1000px;
            transition: color 0.35s ease;
          }

          .file-sub-date {
            font-size: 14px;
            color: #888888;
            transition: color 0.35s ease;
          }
        }
      }

      .card-right {
        .btn-download-outline {
          height: 42px;
          padding: 0 24px;
          border: 1px solid #222222;
          background: #ffffff;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          color: #222222;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);

          i {
            margin-left: 6px;
            transition: transform 0.35s ease;
          }
        }
      }

      /* 鼠标悬停到文件卡片时：仅文件名与下载按钮联动丝滑变色，主盒子保持原色 */
      &:hover {
        .pdf-icon-svg {
          transform: scale(1.05);
        }

        .card-left .card-text .file-name-title {
          color: #00d8c3;
        }

        .card-right .btn-download-outline {
          border-color: #00d8c3;
          background-color: #00d8c3;
          color: #ffffff;

          i {
            transform: translateY(2px);
          }
        }
      }
    }
  }
}
</style>